import { useCallback, useEffect } from "react";
import {
  Container,
  Row,
  Form,
  FloatingLabel,
  Button,
  Spinner,
  Col,
  Card,
} from "react-bootstrap";
import { SubmitHandler, useForm } from "react-hook-form";
import { BsKey } from "react-icons/bs";
import { useDispatch, useSelector } from "react-redux";
import styles from "./changePasswordPage.module.scss";
import changePasswordPageSlice from "./store/slice";

export type ChangePasswordFormValues = {
  existing_password: string;
  password: string;
  password_confirmation: string;
};

const ChangePasswordPage = () => {
  const dispatch = useDispatch();

  const isLoading = useSelector((state) => state.changePasswordPage.isLoading);

  const sendRequest = useCallback(
    (data: ChangePasswordFormValues) =>
      dispatch(changePasswordPageSlice.actions.sendRequest(data)),
    [dispatch]
  );

  const reset = useCallback(
    () => dispatch(changePasswordPageSlice.actions.reset()),
    [dispatch]
  );

  const {
    formState: { errors },
    register,
    handleSubmit,
    watch,
  } = useForm<ChangePasswordFormValues>();

  const onSubmit: SubmitHandler<ChangePasswordFormValues> = (data) => {
    sendRequest(data);
  };

  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  return (
    <Container fluid>
      <Row>
        <Col />
        <Col lg={4}>
          <Card className={styles.card}>
            <Card.Body>
              <Card.Title>
                <BsKey /> Change password
              </Card.Title>

              <hr />

              <Form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
                <FloatingLabel
                  controlId="existing_password"
                  label="Existing password"
                  className={styles.input}
                >
                  <Form.Control
                    type="password"
                    placeholder="Existing password"
                    isInvalid={!!errors.existing_password}
                    {...register("existing_password", {
                      required: true,
                    })}
                    maxLength={100}
                    disabled={isLoading}
                  />
                  <Form.Control.Feedback
                    type="invalid"
                    className={styles.feedback}
                  >
                    {errors.password?.type === "required" && "Required."}
                  </Form.Control.Feedback>
                </FloatingLabel>

                <FloatingLabel
                  controlId="password"
                  label="Password"
                  className={styles.input}
                >
                  <Form.Control
                    type="password"
                    placeholder="Password"
                    isInvalid={!!errors.password}
                    {...register("password", {
                      required: true,
                      minLength: 8,
                      validate: {
                        containsUpper: (value) => /[A-Z]/.test(value),
                        containsLower: (value) => /[a-z]/.test(value),
                        containsDigits: (value) => /[0-9]/.test(value),
                      },
                    })}
                    maxLength={100}
                    disabled={isLoading}
                  />
                  <Form.Control.Feedback
                    type="invalid"
                    className={styles.feedback}
                  >
                    {errors.password?.type === "required" && "Required."}
                    {errors.password?.type === "minLength" &&
                      "Minimum 8 characteres required."}
                    {errors.password?.type === "containsUpper" &&
                      "At least 1 upper case letter required."}
                    {errors.password?.type === "containsLower" &&
                      "At least 1 lower case letter required."}
                    {errors.password?.type === "containsDigits" &&
                      "At least 1 number required."}
                  </Form.Control.Feedback>
                </FloatingLabel>

                <FloatingLabel
                  controlId="password_confirmation"
                  label="Confirm password"
                  className={styles.input}
                >
                  <Form.Control
                    type="password"
                    placeholder="Confirm password"
                    isInvalid={!!errors.password_confirmation}
                    {...register("password_confirmation", {
                      required: true,
                      minLength: 8,
                      validate: {
                        validate: (value) => value === watch("password"),
                      },
                    })}
                    maxLength={100}
                    disabled={isLoading}
                  />
                  <Form.Control.Feedback
                    type="invalid"
                    className={styles.feedback}
                  >
                    {errors.password_confirmation?.type === "required" &&
                      "Required."}
                    {errors.password_confirmation?.type === "validate" &&
                      "Passwords doesn't match."}
                    {errors.password_confirmation?.type === "minLength" &&
                      "Minimum 8 characteres required."}
                  </Form.Control.Feedback>
                </FloatingLabel>

                <Button type="submit" disabled={isLoading}>
                  Change
                  {isLoading && (
                    <Spinner
                      size="sm"
                      animation="border"
                      className={styles.spinner}
                    />
                  )}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>
        <Col />
      </Row>
    </Container>
  );
};

export default ChangePasswordPage;
